import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NavigationEnd, Params} from '@angular/router';
import {
  CatalogFeature,
  CustomerGroup,
  IdAndHeader,
  ListFilterService,
  ListTable,
  PageApiResponse,
  ProductCustomer,
  StoreService
} from 'compob2b-libs';
import { Observable } from 'rxjs';
import {catchError, filter, map, startWith, switchMap, take, takeUntil, tap, toArray} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {BreadcrumbService} from '../../breadcrumbs/breadcrumbs.service';
import {UtilsService} from '../../utils/utils.service';
import { DeviceService } from "../../../../../../compob2b-libs/src/lib/services/device.service";
import { CATALOG_ROOT_ID } from "../services/customer-catalog.service";
import { CatalogMobileSelectorService } from "../services/catalog-mobile-selector.service";
import { CatalogMobileFilterComponent } from "../catalog-mobile-filter/catalog-mobile-filter.component";

@Component({
    selector: 'app-catalog-list',
    templateUrl: './catalog-list.component.html',
    styleUrl: './catalog-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CatalogListComponent extends ListTable implements OnInit, OnDestroy {

  productList$: Observable<ProductCustomer[]>;
  customerGroups: CustomerGroup;
  queryParams: Params;
  currentCategoryId: number;
  isShowcase: boolean = false;
  isLeafCategory: boolean = false;
  isShowProductList = localStorage.getItem('pimBlock') !== '1';
  catalog: IdAndHeader;
  catalogFeature: CatalogFeature;
  showSubcategories: boolean = localStorage.getItem('showCatalogTiles') !== '0';
  @ViewChild(CatalogMobileFilterComponent) mobileFilter: CatalogMobileFilterComponent;

  constructor(public productFilterService: ListFilterService,
              private readonly breadcrumbService: BreadcrumbService,
              public device: DeviceService,
              private readonly storeService: StoreService,
              private readonly catalogMobileSelectorService: CatalogMobileSelectorService
  ) {
    super();
  }

  openCatalogSelector(): void {
    this.catalogMobileSelectorService.nextOpenSelector();
  }

  openMobileFilter(): void {
    this.mobileFilter?.open();
  }

  ngOnInit() {
    this.breadcrumbService.viewBreadcrumbs(true);
    if (this.device.isMobile) {
      this.isShowProductList = false;
    }
    this.route.data
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(result => {
        if (result) {
          this.isShowcase = result.showcase;
        }
      });

    this.storeService.getCustomerGroup()
      .pipe(takeUntil((this.unsubscribe)))
      .subscribe(item => {
        this.customerGroups = item;

        this.router.navigate(['catalog'], {queryParams: {region: item.id}, queryParamsHandling: 'merge'});
      });

    this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
      const queryParams: HttpParams = UtilsService.buildQueryParams(params);

      if (queryParams.get('view') !== null && queryParams.get('view') === 'list') {
        this.isShowProductList = true;
      }

      if (queryParams.get('view') !== null && queryParams.get('view') === 'block') {
        this.isShowProductList = false;
      }
    });

    // Крошки грузим по NavigationEnd, а не по queryParams: BreadcrumbService сбрасывает
    // currentBreadcrumbs именно на NavigationEnd (как root-синглтон подписан раньше нас),
    // поэтому наш подписчик всегда отработает ПОСЛЕ его сброса и не даст этому сбросу
    // затереть уже применённую цепочку крошек. startWith нужен для первого захода: к
    // моменту нашей подписки NavigationEnd текущей навигации мог уже случиться.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.route.snapshot.queryParams),
        startWith(this.route.snapshot.queryParams),
        takeUntil(this.unsubscribe)
      )
      .subscribe(params => this.loadBreadCrumbs(params));

    this.productList$ = this.route.queryParams
      .pipe(
        switchMap(params => {
          return this.loadList(params);
        }),
      );
  }

  ngOnDestroy() {
    this.productFilterService.reset();
    this.cdr.detectChanges();
    super.ngOnDestroy();
  }

  showProductBlocks() {
    this.isShowProductList = !this.isShowProductList;

    localStorage.setItem('pimBlock', this.isShowProductList ? null : '1');
    this.cdr.detectChanges();

    //
    //
    // this.router.navigate(['.'], {
    //   relativeTo: this.route,
    //   queryParams: {
    //     view: idViewBlock ? 'block' : 'list'
    //   },
    //   queryParamsHandling: 'merge'
    // });
  }

  private loadBreadCrumbs(params: Params): void {
    this.queryParams = params;
    this.catalogFeature = null

    if (!params.catalog_id) {
      this.catalog = null;
      this.storeService.setTitle(translations.chooseProducts);
      this.currentCategoryId = CATALOG_ROOT_ID
      return;
    }

    this.currentCategoryId = params.catalog_id;

    this.breadcrumbService.getCatalogBreadCrumbs(params.catalog_id)
      .pipe(
        toArray(),
        tap(chain => this.handleCatalogChain(chain))
      ).subscribe();
  }

  private handleCatalogChain(chain: CatalogFeature[]): void {
    if (!chain.length) {
      return;
    }

    // корневая категория цепочки заменяет статичный сегмент роута
    this.breadcrumbService.set('/catalog', {skip: true});

    chain.forEach(catalog => this.breadcrumbService.setWithParams(
      '/catalog',
      catalog.header,
      {
        queryParams: {
          catalog_id: catalog.id,
          region: this.customerGroups?.id
        }
      }
    ));

    this.handleLastCatalogEmit(chain[chain.length - 1]);
  }

  private handleLastCatalogEmit(catalog: any): void {
    this.catalogFeature = catalog;

    const title = catalog.htHead?.length ? catalog.htHead : catalog.header;

    this.storeService.setTitle(title);
    this.storeService.setDescription(catalog.htDesc || '');
    this.storeService.setKeywords(catalog.htKeywords || '');

    this.catalog = { id: catalog.id, header: catalog.header };
  }

  private loadList(params: Params) {
    let queryParams: HttpParams = UtilsService.buildQueryParams(params);
    queryParams = queryParams.set('region', queryParams.get('region') || this.customerGroups?.id);
    queryParams = queryParams.set('limit', queryParams.get('limit') || this.pagination.limit);

    return this.compoApiService.getWithParams<PageApiResponse<ProductCustomer[]>>('/product/filter', queryParams)
      .pipe(
        take(1),
        catchError(err => this.handleError(err)),
        tap(response => this.handleResponse(response.data)),
        map(response => response.data?.content),
        tap(productList => {
          if (productList?.length) {
            this.storeService.nextCurrentProduct(productList[0].syncUid);
          }
        })
      );
  }

  onIsLeaf($event: boolean) {
    this.isLeafCategory = $event;
    this.cdr.markForCheck();
  }

  toggleSubcategories(): void {
    this.showSubcategories = !this.showSubcategories;
    localStorage.setItem('showCatalogTiles', this.showSubcategories ? '1' : '0');
  }
}
